import type { OpKind, Session } from './types'
import { queueAgendaStep, type AgendaStep } from './agenda'
import {
  assessmentFingerprint,
  assessmentIsCurrent,
  type AssessmentAction,
  type AssessmentAttempt,
} from './runAssessment'
import { mathHead } from './mathematics'

export interface AssessmentAgendaBinding {
  assessmentId: string
  actionId: string
  packetRevision: string
}

export function assessmentBindingIssue(
  session: Session,
  binding: AssessmentAgendaBinding,
): string | null {
  const attempt = session.research?.assessments?.attempts.find((a) => a.id === binding.assessmentId)
  if (!attempt || attempt.state !== 'completed' || !attempt.result)
    return 'The linked research assessment is unavailable or unfinished.'
  if (attempt.packet.revision !== binding.packetRevision || !assessmentIsCurrent(session, attempt))
    return 'The assessment inputs changed. Review the current evidence before queueing a new action.'
  if (!attempt.result.next.some((a) => a.id === binding.actionId))
    return 'The assessment does not contain this proposed action.'
  return null
}

/** Only an explicit queue press creates this record. Queueing itself spends
 * nothing; the existing agenda executor owns authorization and prerequisites. */
export function queueAssessmentAction(
  session: Session,
  attempt: AssessmentAttempt,
  action: AssessmentAction,
  now: number,
) {
  const binding = {
    assessmentId: attempt.id,
    actionId: action.id,
    packetRevision: attempt.packet.revision,
  }
  const issue = assessmentBindingIssue(session, binding)
  if (issue) throw new Error(issue)
  const saved = session.research!.assessments!.attempts.find((a) => a.id === attempt.id)!
  const retained = saved.result!.next.find((a) => a.id === action.id)!
  const math = session.research?.mathematics
  const targetRef = retained.targetObligationId
    ? saved.packet.references.find((r) => r.id === retained.targetObligationId)
    : null
  const target = targetRef
    ? (JSON.parse(targetRef.content) as { id: string; revision: string })
    : null
  const current = target && math ? mathHead(math.obligations, target.id) : null
  if (target && (!current || current.revision !== target.revision))
    throw new Error('The proposed mathematical target changed or is unavailable.')
  const mathematics = current
    ? {
        target: { id: current.id, revision: current.revision },
        requires: current.requires,
        plan: null,
      }
    : undefined
  const purpose = `${retained.question}\n\n${retained.purpose}`
  if (purpose.length > 2000)
    throw new Error(
      'The proposed purpose exceeds the agenda allowance; prepare a focused revision.',
    )
  return queueAgendaStep(session, {
    id: `assessment-step:${assessmentFingerprint(binding)}`,
    nodeId: retained.nodeId,
    action: retained.action,
    purpose,
    priority: 'uncertainty',
    assessment: binding,
    ...(mathematics ? { mathematics } : {}),
    at: now,
  })
}

/** Exact selected evidence and the negative result's repair requirements travel
 * into the real next paid request. Merely showing them in a report is not reuse. */
export function assessmentActionContext(
  session: Session,
  nodeIds: readonly string[],
  action?: OpKind,
) {
  return (session.research?.agenda?.steps ?? [])
    .filter(
      (step) =>
        nodeIds.includes(step.nodeId) &&
        (!action || step.action === action) &&
        step.assessment &&
        ['pending', 'running'].includes(step.status),
    )
    .map((step: AgendaStep) => {
      const binding = step.assessment!
      const issue = assessmentBindingIssue(session, binding)
      if (issue) return { binding, issue }
      const attempt = session.research!.assessments!.attempts.find(
        (a) => a.id === binding.assessmentId,
      )!
      const action = attempt.result!.next.find((a) => a.id === binding.actionId)!
      const finding = attempt.result!.findings.find((f) => f.id === action.findingId)!
      const lessons = attempt.result!.lessons.filter(
        (l) =>
          action.lessonIds.includes(l.id) ||
          l.failedNodeId === action.nodeId ||
          l.findingId === finding.id,
      )
      const ids = new Set([
        ...finding.evidenceIds,
        ...action.evidenceIds,
        ...lessons.flatMap((l) => l.evidenceIds),
      ])
      return {
        binding,
        authority:
          'AI assessment and proposed action; mathematical conclusions require their own evidence and interpretation.',
        finding,
        action,
        lessons,
        evidence: attempt.packet.references.filter((r) => ids.has(r.id)),
        missingContext: [...attempt.packet.missingContext, ...attempt.result!.missingContext],
      }
    })
}
